var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Logging, PropertyConverters, Net, Utils, P, PCSS, CustomElement, Watch, PropertyChangeEvent, PropertyChangeEventName, ViewChild, CustomElementUtils, JsonFunctionConversion } from '@pacem/pacem-core';
import { Components as ComponentsUI } from '@pacem/pacem-ui';
import { PacemFormRelevantElement } from './form';
import { ContenteditableUtils } from './contenteditable/utils';
import { PacemBaseValidatorElement, PacemLengthValidatorElement, PacemAsyncValidatorElement, PacemBinaryValidatorElement, PacemCompareValidatorElement, PacemCustomValidatorElement, PacemRangeValidatorElement, PacemRegexValidatorElement, PacemRequiredValidatorElement } from './validators';
//namespace Components.Scaffolding {
function getFormId(key) {
    return 'form' + key;
}
function patternToString(pattern) {
    return new RegExp(pattern /*.split('\\').join('\\\\')*/).source;
}
function isKnownValidator(v) {
    return 'errorMessage' in v;
}
function isValidatorFactory(v) {
    return 'attributes' in v && typeof v.attributes === 'function';
}
let PacemFormFieldElement = class PacemFormFieldElement extends PacemFormRelevantElement {
    constructor(_md = new ComponentsUI.UI.MarkdownService()) {
        super();
        this._md = _md;
        this._entityPropertyChangeHandler = (e) => {
            // nudge entity propertychange notification
            this.dispatchEvent(new PropertyChangeEvent({ propertyName: 'entity', currentValue: this.entity }));
        };
        this._labelClickHandler = (_) => {
            document.getElementById(this._label.htmlFor).focus();
        };
        this._key = '_' + Utils.uniqueCode();
    }
    get key() {
        return this._key;
    }
    get fetcher() {
        return this._fetcher;
    }
    get field() {
        return this._field;
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._buildUpForm();
        this._buildUpFetcher();
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        switch (name) {
            case 'readonly':
                this._ensureBalloon();
                break;
            case 'metadata':
                this._ensureBalloon();
                this._buildUpCss();
                this._buildUpLabel();
                this._buildUpField();
                break;
            case 'entity':
                // In the case that the entity is a HTMLElement,
                // its changes might not propagate correctly.
                // This is a nudgy solution. Explore how to improve
                // `CustomElementUtils.set` as an alternative...
                if (old instanceof HTMLElement) {
                    old.removeEventListener(PropertyChangeEventName, this._entityPropertyChangeHandler, false);
                }
                if (val instanceof HTMLElement) {
                    val.addEventListener(PropertyChangeEventName, this._entityPropertyChangeHandler, false);
                }
                break;
        }
    }
    disconnectedCallback() {
        if (this.entity instanceof HTMLElement) {
            this.entity.removeEventListener(PropertyChangeEventName, this._entityPropertyChangeHandler, false);
        }
        if (!Utils.isNull(this._label)) {
            this._label.removeEventListener('click', this._labelClickHandler, false);
        }
        if (!Utils.isNull(this._balloon)) {
            this._balloon.remove();
        }
        super.disconnectedCallback();
    }
    _normalizeTooltip() {
        const metadata = this.metadata && this.metadata.extra && this.metadata.extra.tooltip || false;
        if (typeof metadata === 'object') {
            return metadata;
        }
        return { type: metadata };
    }
    _ensureBalloon() {
        var innerText, tooltip;
        const noBalloon = this.readonly
            || ((tooltip = this._normalizeTooltip()).type === false)
            || Utils.isNullOrEmpty(innerText = this.metadata && this.metadata.display && this.metadata.display.description);
        // balloon
        if (Utils.isNull(this._balloon) && !noBalloon) {
            let balloon = document.createElement(P + '-balloon');
            balloon.options = {
                behavior: ComponentsUI.UI.BalloonBehavior.Tooltip,
                trigger: tooltip.trigger || ComponentsUI.UI.BalloonTrigger.Hover,
                position: tooltip.position || ComponentsUI.UI.BalloonPosition.Top,
                hoverDelay: 200, hoverTimeout: 50,
                align: tooltip.align || ComponentsUI.UI.BalloonAlignment.Auto
            };
            Utils.addClass(balloon, PCSS + '-field-tooltip');
            var shell = CustomElementUtils.findAncestorShell(this);
            shell.appendChild(this._balloon = balloon);
        }
        const balloon = this._balloon;
        if (!Utils.isNull(balloon)) {
            balloon.target = this._label;
            if (!(balloon.disabled = noBalloon)) {
                const content = innerText || '';
                switch (tooltip.type) {
                    case 'md':
                    case 'markdown':
                        balloon.innerHTML = this._md.toHtml(content);
                        break;
                    case 'html':
                        balloon.innerHTML = content;
                        break;
                    default:
                        balloon.innerText = content;
                        break;
                }
            }
        }
    }
    _buildUpCss() {
        const display = this.metadata?.display;
        if (Utils.isNull(display)) {
            return;
        }
        if (typeof display.cssClass === 'function') {
            const { cssClassAttr } = display.cssClass(this, '$this', '$this.entity');
            this.setAttribute('css-class', cssClassAttr);
        }
        else {
            this.cssClass = display.cssClass;
        }
        if (typeof display.css === 'function') {
            const { cssAttr } = display.css(this, '$this', '$this.entity');
            this.setAttribute('css', cssAttr);
        }
        else {
            this.css = display.css;
        }
    }
    _buildUpLabel() {
        // label
        const label = this._label, span = label.firstElementChild;
        const meta = this.metadata;
        label.htmlFor = this._key;
        Utils.addClass(label, PCSS + '-label');
        const vals = this.metadata.validators;
        if (vals && vals.find(v => v.type === 'required')) {
            Utils.addClass(label, PCSS + '-required');
        }
        else {
            Utils.removeClass(label, PCSS + '-required');
        }
        //
        if (!Utils.isNull(this._balloon) && !this._balloon.disabled) {
            Utils.addClass(label, PCSS + '-tooltip');
        }
        else {
            Utils.removeClass(label, PCSS + '-tooltip');
        }
        // exploit a pacem-span so that it cleans the htm content up
        if (typeof meta.display?.name === 'function') {
            const { contentAttr } = meta.display.name(this, ':host', ':host.entity', '::_form');
            span.setAttribute('content', contentAttr);
        }
        else {
            span.removeAttribute('content');
            span.content = (meta.display && meta.display.name) || meta.prop;
        }
        label.setAttribute('id', 'label' + this._key);
        label.addEventListener('click', this._labelClickHandler, false);
    }
    // #region Template-called
    _isValueNullOrEmpty(entity = this.entity, metadata = this.metadata) {
        if (Utils.isNull(metadata)) {
            return true;
        }
        const props = metadata.prop.split('.');
        let obj = entity;
        for (let j = 0; j < props.length; j++) {
            const prop = props[j];
            if (Utils.isNullOrEmpty(obj && obj[prop])) {
                return true;
            }
            // next
            obj = obj[prop];
        }
        return false;
    }
    /**
     * Reflects the value onto the entity attribute, if the 'entity' is an instance of HTMLElement.
     */
    _handleValueChange(evt) {
        if (evt.detail.propertyName === 'value') {
            const value = evt.detail.currentValue, prop = this.metadata.prop;
            if (this.entity instanceof HTMLElement) {
                // handle the entity as an element and modify the attribute
                let attr = CustomElementUtils.camelToKebab(prop);
                if (Utils.isNullOrEmpty(value)) {
                    this.entity.removeAttribute(attr);
                }
                else {
                    this.entity.setAttribute(attr, value.toString());
                }
            }
            else {
                this.entity[prop] = value;
            }
        }
    }
    /**
     * 'Prettifies' the input value (for some specific cases) to be included in an URL segment or query string.
     * @param v Input value
     */
    _adjustDependencyValue(v) {
        if (Utils.Dates.isDate(v)) {
            return Utils.Dates.parse(v).toISOString();
        }
        return v;
    }
    _broadcastFetchResult(v) {
        const field = this;
        this.dispatchEvent(new CustomEvent("fetch", { detail: v, bubbles: true, cancelable: false }));
    }
    // #endregion
    _buildUpFetcher() {
        // fetcher
        var fetcher = this._fetcher;
        fetcher.setAttribute('id', 'fetch' + this._key);
        fetcher.setAttribute('on-fetchresult', ':host._broadcastFetchResult($event.detail)');
    }
    _buildUpForm() {
        // form
        var form = this._form;
        form.setAttribute('id', getFormId(this._key));
        form.setAttribute('sudden-validation', '{{ ::form.suddenValidation }}');
    }
    _apiResult(v) {
        return Utils.getApiResult(v);
    }
    _buildUpField() {
        if (this._field)
            this._field.remove();
        this._fetcher.removeAttribute('parameters');
        this._fetcher.result =
            this._fetcher.url = null;
        for (var j = this._validators.children.length - 1; j >= 0; j--)
            this._validators.children[j].remove();
        var meta = this.metadata;
        // field
        let tagName = /* setting the default/fallback input element */ P + '-input-text';
        let numericTagName = P + (meta.display && meta.display.ui === 'slider' ? '-slider' : '-input-number');
        let attrs = {
            'id': this._key, 'name': meta.prop,
            // readonly if property `readonly` set to true OR metadata property is not editable OR parent form's `readonly` property is set to true
            'readonly': "{{ :host.readonly || :host.metadata.isReadOnly || ::_form.readonly }}",
            'value': `{{ :host.entity.${meta.prop}, twoway }}`,
            'placeholder': `${((meta.display && meta.display.watermark) || "")}`
        };
        // autofocus
        if (meta.extra?.autofocus == true) {
            attrs['on-load'] = 'requestAnimationFrame(() => $this.focus())';
        }
        // fetch data
        let fetchData = meta.extra || {};
        let fetchAttrs = {};
        let disabledAttr = "false";
        let dependingClause = '';
        let dependingDisabling = '';
        let dependingParameters = '';
        let dependsOn = [];
        // #region dependency from other props
        if (!Utils.isNullOrEmpty(fetchData.dependsOn)) {
            if (typeof fetchData.dependsOn === 'function') {
                const depAttrs = fetchData.dependsOn(this, ':host', ':host.entity', '$this.entity'), hideAttr = depAttrs.hideAttr;
                if (!Utils.isNullOrEmpty(hideAttr)) {
                    this.setAttribute('hide', hideAttr);
                }
                if (!Utils.isNullOrEmpty(depAttrs.disabledAttr)) {
                    disabledAttr = depAttrs.disabledAttr;
                    if (CustomElementUtils.isBindingAttribute(disabledAttr)) {
                        dependingDisabling = CustomElementUtils.extractBindingAttributeExpression(disabledAttr);
                    }
                }
                if (!Utils.isNullOrEmpty(depAttrs.parameterAttrs)) {
                    const pAttrs = depAttrs.parameterAttrs, parameterPairs = [];
                    for (let alias in pAttrs) {
                        const propName = pAttrs[alias];
                        parameterPairs.push(`${alias}: ${propName}`);
                        // exploit this sneaky way...
                        dependsOn.push({ prop: propName });
                    }
                    dependingParameters = parameterPairs.join(', ');
                }
            }
            else {
                dependsOn = fetchData.dependsOn;
                // there should be a better way...
                let disablingClauses = { empty: [], notEqual: {} };
                let hidingClauses = { empty: [], notEqual: {} };
                // fetch
                let namesAndPaths = [];
                let paths = [];
                for (var depends of dependsOn) {
                    let path = ':host.entity.' + depends.prop, path2 = '$this.entity.' + depends.prop;
                    if (!Utils.isNullOrEmpty(depends.value)) {
                        let clause = (p) => p + ' !== ' + JSON.stringify(depends.value);
                        (disablingClauses.notEqual[path] = disablingClauses.notEqual[path] || []).push(clause(path));
                        if (depends.hide) {
                            (hidingClauses.notEqual[path2] = hidingClauses.notEqual[path2] || []).push(clause(path2));
                        }
                    }
                    else if (!depends.optional) {
                        let clause = (p) => '$pacem.isNullOrEmpty(' + p + ')';
                        disablingClauses.empty.push(clause(path));
                        if (depends.hide) {
                            hidingClauses.empty.push(clause(path2));
                        }
                    }
                    // fetch
                    paths.push(path);
                    namesAndPaths.push(`${(depends.alias || depends.prop)}: :host._adjustDependencyValue(${path})`);
                    if (!depends.optional) {
                        dependingClause += `!$pacem.isNullOrEmpty(${path}) && `;
                    }
                }
                dependingParameters = namesAndPaths.join(', ');
                let joinClauses = (d) => {
                    let ne = 'false', emp = 'false';
                    if (!Utils.isNullOrEmpty(d.notEqual)) {
                        // when a value is provided then pick the logical operator when provided.
                        // consider to switch semantic (i.e. 'or' -> '&&', 'and' -> '||') due to the 'notEqual' operator.
                        // 'and' is assumed to be the default
                        const neByProp = [];
                        for (let dp in d.notEqual) {
                            // same property conditions always concatenated with '&&' (logical and)
                            neByProp.push('(' + d.notEqual[dp].join(' && ') + ')');
                        }
                        // different property clauses should be concatenated with '||' (logical or)
                        ne = '(' + neByProp.join(' || ') + ')';
                    }
                    if (!Utils.isNullOrEmpty(d.empty)) {
                        // logical operator doe not affect 'empty' (no 'value' explicitated) deps:
                        // empty always means 'BAD', then ANY empty entry would automatically trigger a negative outcome ('||').
                        emp = '(' + d.empty.join(' || ') + ')';
                    }
                    return ne + ' || ' + emp;
                };
                dependingDisabling = joinClauses(disablingClauses);
                disabledAttr = `{{ ${dependingDisabling} }}`;
                // never disable the form-field, NEVER!
                // attrs['disabled'] = disabledAttr; // * BUG
                this.setAttribute('hide', `{{ ${joinClauses(hidingClauses)} }}`);
            }
        }
        // #endregion
        const fn = 'fn';
        const fns = this[fn] = this[fn] || {};
        const spinStaticDatasourceAttr = (source = []) => {
            let args = (dependsOn || []).map(p => ':host.entity.' + p.prop).join(', ');
            const fnKey = 'fn' + Utils.uniqueCode();
            switch (typeof source) {
                case 'function':
                    fns[fnKey] = source;
                    break;
                default:
                    if (Utils.isArray(source)) {
                        fns[fnKey] = () => source;
                    }
                    else {
                        throw 'Unsupported source format.';
                    }
                    break;
            }
            return `{{ :host.${fn}.${fnKey}(${args}) }}`;
        };
        const children = new DocumentFragment();
        let uiHint;
        // metadata
        if (typeof meta.type === 'function' /* < factory */) {
            // build-up meta-structure
            var retval = meta.type(this, ':host', ':host.entity');
            // element must be a registered custom-element (hyphen!)
            if (retval.tagName.indexOf('-') === -1) {
                this.log(Logging.LogLevel.Error, `${tagName} is not an allowed form-field`);
            }
            else {
                // set tagName
                tagName = retval.tagName;
                // set/override attributes
                Utils.extend(attrs, retval.attrs || {});
                // prepare recursive seeding function
                function seedChildren(parent, children) {
                    if (!Utils.isNullOrEmpty(children)) {
                        for (let child of children) {
                            // element must be a registered custom-element (hyphen!)
                            if (child.tagName.indexOf('-') === -1) {
                                this.log(Logging.LogLevel.Error, `${child.tagName} is not an allowed child element for form-field ${tagName}`);
                                continue;
                            }
                            const el = document.createElement(child.tagName);
                            if (!Utils.isNullOrEmpty(child.attrs)) {
                                for (let attr in child.attrs) {
                                    const attrVal = child.attrs[attr];
                                    el.setAttribute(attr, attrVal);
                                }
                            }
                            if (!Utils.isNullOrEmpty(child.children)) {
                                // recursion here
                                seedChildren(el, child.children);
                            }
                            parent.appendChild(el);
                        }
                    }
                }
                // seed
                seedChildren(children, retval.children);
            }
        }
        else
            switch (uiHint = (meta.display && meta.display.ui)) {
                // remove this (use dataType = 'HTML' instead).
                case 'contentEditable':
                    console.warn('`contentEditable` ui hint is deprecated. Lean on `dataType` equal to \'HTML\' instead.');
                    tagName = P + '-contenteditable';
                    children.append(ContenteditableUtils.getDefaultDashboard());
                    break;
                case 'snapshot':
                    tagName = P + '-snapshot';
                    if (fetchData.width > 0) {
                        attrs['width'] = '' + fetchData.width;
                    }
                    if (fetchData.height > 0) {
                        attrs['height'] = '' + fetchData.height;
                    }
                    if (fetchData.quality >= 0) {
                        attrs['quality'] = '' + fetchData.quality;
                    }
                    break;
                case 'chars':
                case 'digits':
                case 'otp':
                    tagName = P + '-input-chars';
                    if (fetchData.charCount > 0) {
                        attrs['charCount'] = '' + fetchData.charCount;
                    }
                    if (!Utils.isNullOrEmpty(fetchData.inputTransform)) {
                        attrs['input-transform'] = fetchData.inputTransform;
                    }
                    break;
                case 'oneToMany':
                case 'dropdown':
                case 'radioList':
                case 'checkboxList':
                case 'manyToMany':
                    if (uiHint === 'oneToMany' || uiHint === 'dropdown') {
                        // select
                        tagName = P + '-select';
                        if (fetchData.preventWheel !== false /* must explicit a 'false': default/empty is true for <select> */) {
                            attrs['on-wheel'] = '$event.preventDefault()';
                        }
                    }
                    else {
                        // checkboxlist
                        delete attrs['placeholder'];
                        tagName = P + (uiHint === 'radioList' ? '-radio-list' : '-checkbox-list');
                    }
                    if (!Utils.isNullOrEmpty(fetchData.textProperty)) {
                        attrs['text-property'] = fetchData.textProperty;
                    }
                    if (!Utils.isNullOrEmpty(fetchData.disabledProperty)) {
                        attrs['disabled-property'] = fetchData.disabledProperty;
                    }
                    if (!Utils.isNullOrEmpty(fetchData.valueProperty)) {
                        if (!meta.isComplexType)
                            attrs['value-property'] = fetchData.valueProperty;
                        else
                            attrs['compare-by'] = fetchData.valueProperty;
                    }
                    if (Utils.isNullOrEmpty(fetchData.source)) {
                        // datasource has to be fetched
                        this._fetcher.id = `fetch${this._key}`;
                        this._fetcher.url = fetchData.sourceUrl;
                        this._fetcher.method = fetchData.verb;
                        //
                        fetchAttrs['parameters'] = `{{ { ${dependingParameters} } }}`;
                        fetchAttrs['disabled'] = disabledAttr;
                        attrs['datasource'] = `{{ ${dependingClause}:host._apiResult(#fetch${this._key}.result) || null }}`;
                    }
                    else {
                        // static datasource provided
                        attrs['datasource'] = spinStaticDatasourceAttr(fetchData.source);
                    }
                    break;
                // experimental (do not include in metadata)
                //case 'hierarchy':
                //    // select
                //    delete attrs['placeholder'];
                //        tagName = P + '-hierarchy';
                //    if (!Utils.isNullOrEmpty(fetchData.textProperty)) {
                //        attrs['text-property'] = fetchData.textProperty;
                //    }
                //    if (!Utils.isNullOrEmpty(fetchData.disabledProperty)) {
                //        attrs['disabled-property'] = fetchData.disabledProperty;
                //    }
                //    if (!Utils.isNullOrEmpty(fetchData.parentsProperty)) {
                //        attrs['parents-property'] = fetchData.parentsProperty;
                //    }
                //    if (!Utils.isNullOrEmpty(fetchData.valueProperty)) {
                //        attrs['value-property'] = fetchData.valueProperty;
                //    }
                //    if (!Utils.isNullOrEmpty(fetchData.unlinkCaption)) {
                //        attrs['unlink-caption'] = fetchData.unlinkCaption;
                //    }
                //    if (Utils.isNullOrEmpty(fetchData.source)) {
                //        // datasource has to be fetched
                //        this._fetcher.id = `fetch${this._key}`;
                //        this._fetcher.url = fetchData.sourceUrl;
                //        this._fetcher.method = fetchData.verb;
                //        //
                //        fetchAttrs['parameters'] = `{{ { ${dependingParameters} } }}`;
                //        fetchAttrs['disabled'] = disabledAttr;
                //        attrs['datasource'] = `{{ ${dependingClause}Pacem.Utils.getApiResult(#fetch${this._key}.result) || null }}`;
                //    } else {
                //        // static datasource provided
                //        attrs['datasource'] = spinStaticDatasourceAttr(fetchData.source);
                //    }
                //    break;
                case 'suggest':
                case 'tags':
                case 'autocomplete':
                    // autocomplete
                    const tags = meta.display.ui === 'tags';
                    tagName = P + (tags ? '-tags' : '-suggest');
                    this._fetcher.id = `fetch${this._key}`;
                    if (!Utils.isNullOrEmpty(fetchData.textProperty)) {
                        attrs['text-property'] = fetchData.textProperty;
                    }
                    let itemValue = `#${this._key}.value`;
                    if (!Utils.isNullOrEmpty(fetchData.valueProperty)) {
                        attrs['compare-by'] = fetchData.valueProperty;
                        if (tags) {
                            itemValue = `(${itemValue} && ${itemValue}.${fetchData.valueProperty}) || ''`;
                        }
                        else {
                            if (!meta.isComplexType) {
                                attrs['value-property'] = fetchData.valueProperty;
                            }
                            else {
                                itemValue += ` && ${itemValue}.${fetchData.valueProperty}`;
                            }
                        }
                    }
                    if (!Utils.isNullOrEmpty(fetchData.sourceUrl)) {
                        fetchAttrs['url'] = `${fetchData.sourceUrl}`;
                        this._fetcher.method = fetchData.verb;
                        //
                        if (!Utils.isNullOrEmpty(fetchData.dependsOn)) {
                            // deps
                            fetchAttrs['parameters'] = `{{ { ${dependingParameters}, q: #${this._key}.hint || '', ${(fetchData.valueProperty || 'value')}: ${itemValue} || '' } }}`;
                            fetchAttrs['disabled'] = disabledAttr;
                        }
                        else {
                            // no deps
                            fetchAttrs['parameters'] = `{{ {q: #${this._key}.hint || '', ${(fetchData.valueProperty || 'value')}: ${itemValue} || '' } }}`;
                        }
                        attrs['datasource'] = `{{ ${dependingClause}:host._apiResult(#fetch${this._key}.result) || null }}`;
                    }
                    else {
                        // static datasource
                        attrs['datasource'] = spinStaticDatasourceAttr(fetchData.source);
                    }
                    if (tags) {
                        // tags
                        attrs['allow-new'] = (meta.extra.allowNew === true).toString();
                        attrs['allow-duplicates'] = (meta.extra.allowDuplicates === true).toString();
                    }
                    else {
                        // suggest
                        if (!Utils.isNullOrEmpty(fetchData.disabledProperty)) {
                            attrs['disabled-property'] = fetchData.disabledProperty;
                        }
                        if (!Utils.isNullOrEmpty(fetchData.itemtemplate)) {
                            const itmpl = fetchData.itemtemplate;
                            if (itmpl instanceof HTMLElement) {
                                attrs['itemtemplate'] = '{{ #' + (itmpl.id = itmpl.id || this._key + '_itemtmpl') + ' }}';
                                if (!itmpl.isConnected) {
                                    this.append(itmpl);
                                }
                            }
                            else {
                                attrs['itemtemplate'] = itmpl;
                            }
                        }
                        if (!Utils.isNullOrEmpty(fetchData.filterFields)) {
                            const ffields = fetchData.filterFields;
                            attrs['filter-fields'] = (Utils.isArray(ffields)) ? ffields.join(' ') : ffields;
                        }
                        if (!Utils.isNullOrEmpty(fetchData.maxSuggestions)) {
                            attrs['max-suggestions'] = fetchData.maxSuggestions.toString();
                        }
                    }
                    break;
                case "calendar":
                    tagName = P + '-calendar-picker';
                    const calendarFormat = meta.display?.format ?? meta.extra?.format;
                    if (!Utils.isNullOrEmpty(calendarFormat)) {
                        attrs['format'] = '{{ ' + JSON.stringify(calendarFormat) + ' }}';
                    }
                    if (!Utils.isNullOrEmpty(fetchData.disabledRanges)) {
                        attrs['disabled-ranges'] = '{{ ' + JSON.stringify(fetchData.disabledRanges) + ' }}';
                    }
                    break;
                case "dictionary":
                    tagName = P + '-namevalue-list';
                    attrs['dictionary'] = 'true';
                    break;
                case 'switcher':
                    if ((meta.type || '').toLowerCase() === 'boolean') {
                        attrs['class'] = "checkbox-switch";
                    }
                    else {
                        break;
                    }
                default:
                    let dataType = (meta.dataType || meta.type || '').toLowerCase();
                    switch (dataType) {
                        // should replace this one:
                        //case 'imageurl':
                        //    tagName = P + '-input-image';
                        //    const f_id = this._fetcher.id = `fetch${this._key}`;
                        //    attrs['image-set'] = `{{ Pacem.Utils.getApiResult(#${f_id}.result) }}`;
                        //    attrs['on-imagefetchrequest'] = `#${f_id}.url = '${meta.extra.fetchUrl}'; #${f_id}.parameters = { q: $event.detail.hint, skip: $event.detail.skip, take: $event.detail.take }`;
                        //    attrs['max-width'] = '' + meta.extra.width;
                        //    attrs['max-height'] = '' + meta.extra.height;
                        //    attrs['max-thumbnail-height'] = '' + meta.extra.thumbHeight;
                        //    attrs['max-thumbnail-width'] = '' + meta.extra.thumbWidth;
                        //    attrs['upload-url'] = meta.extra.uploadUrl;
                        //    attrs['allow-snapshot'] = '' + meta.extra.snapshot;
                        //    break;
                        case 'upload':
                            // upload
                            tagName = P + '-upload';
                            let uploadExtra = meta.extra || {};
                            attrs['url'] = uploadExtra.uploadUrl;
                            attrs['parallelism'] = '' + uploadExtra.parallelism;
                            attrs['chunk-size'] = '' + uploadExtra.chunkSize;
                            attrs['max-image-width'] = '' + uploadExtra.maxImageWidth;
                            attrs['max-image-height'] = '' + uploadExtra.maxImageHeight;
                            attrs['select-mode'] = uploadExtra.mode ?? 'file';
                            break;
                        case 'html':
                            // contenteditable
                            tagName = P + '-contenteditable';
                            children.append(ContenteditableUtils.getDefaultDashboard());
                            break;
                        case 'enumeration':
                            // radiobutton list
                            tagName = P + '-radio-list';
                            attrs['class'] = PCSS + '-radio-list';
                            attrs['value-property'] = "value";
                            attrs['text-property'] = "caption";
                            attrs['datasource'] = '{{ ' + JSON.stringify(meta.extra.enum) + ' }}';
                            delete attrs['placeholder'];
                            break;
                        case 'password':
                            tagName = P + '-input-password';
                            break;
                        case 'emailaddress':
                            tagName = P + '-input-email';
                            break;
                        case "color":
                            tagName = P + '-input-color';
                            break;
                        case "time":
                        case "date":
                        case "datetime":
                            tagName = P + '-datetime-picker';
                            delete attrs['placeholder'];
                            if (dataType === 'datetime') {
                                attrs['precision'] = "minute";
                            }
                            const dateFormat = meta.display?.format ?? meta.extra?.format;
                            if (!Utils.isNullOrEmpty(dateFormat)) {
                                attrs['format'] = '{{ ' + JSON.stringify(dateFormat) + ' }}';
                            }
                            if (!Utils.isNullOrEmpty(fetchData.disabledRanges)) {
                                attrs['disabled-ranges'] = '{{ ' + JSON.stringify(fetchData.disabledRanges) + ' }}';
                            }
                            break;
                        case "url":
                            tagName = P + '-input-url';
                            break;
                        case "phonenumber":
                            tagName = P + '-input-tel';
                            break;
                        case "multilinetext":
                            tagName = P + '-textarea';
                            break;
                        case "markdown":
                            tagName = P + '-textarea-markdown';
                            break;
                        case 'latlng':
                            tagName = P + '-latlng';
                            if (!Utils.isNullOrEmpty(meta.extra) && typeof meta.extra === 'object' && !Utils.isArray(meta.extra)) {
                                attrs['options'] = JSON.stringify(meta.extra);
                            }
                            break;
                        case 'percent':
                        case 'percentage':
                        case 'currency':
                            let defaultFormatOptions = dataType === 'currency' ? { style: 'currency', currency: 'EUR' } : { style: 'percent', maximumFractionDigits: 2 };
                            let intl = meta.display?.format ?? defaultFormatOptions;
                            attrs['format'] = JSON.stringify(intl);
                        default:
                            switch ((meta.type || '').toLowerCase()) {
                                case "boolean":
                                    tagName = P + '-checkbox';
                                    //attrs['selected'] = `{{ :host.entity.${meta.prop}, twoway }}`;
                                    attrs['true-value'] = "{{ true }}";
                                    attrs['false-value'] = "{{ false }}";
                                    attrs['caption'] = attrs['placeholder'];
                                    //delete attrs['value'];
                                    delete attrs['placeholder'];
                                    break;
                                case "byte":
                                    attrs['min'] = '0';
                                    attrs['max'] = '255';
                                case "int16":
                                case "int32":
                                case "int64":
                                case "short":
                                case "integer":
                                case "int":
                                case "long":
                                    attrs['step'] = '' + (meta.extra?.step ?? '1');
                                    tagName = numericTagName;
                                    const intFormat = meta.display?.format;
                                    if (!Utils.isNullOrEmpty(intFormat)) {
                                        attrs['format'] = JSON.stringify(intFormat);
                                    }
                                    if (fetchData.preventWheel !== false /* must explicit a 'false': default/empty is true for <input[type=number]> */ && tagName === (P + '-input-number')) {
                                        attrs['on-wheel'] = '$event.preventDefault()';
                                    }
                                    break;
                                case "double":
                                case "decimal":
                                case "float":
                                case "single":
                                case "number":
                                    tagName = numericTagName;
                                    attrs['step'] = '' + (meta.extra?.step ?? 'any');
                                    const decimalFormat = meta.display?.format;
                                    if (!Utils.isNullOrEmpty(decimalFormat)) {
                                        attrs['format'] = JSON.stringify(decimalFormat);
                                    }
                                    if (fetchData.preventWheel !== false /* must explicit a 'false': default/empty is true for <input[type=number]> */ && tagName === (P + '-input-number')) {
                                        attrs['on-wheel'] = '$event.preventDefault()';
                                    }
                                    break;
                                default:
                                    if ((meta.type === 'array' || meta.type === 'object') && !Utils.isNullOrEmpty(meta.props)) {
                                        tagName = P + '-childform';
                                        delete attrs['placeholder'];
                                        attrs['metadata'] = Utils.Json.stringify(meta.props, { functions: JsonFunctionConversion.Reference });
                                        attrs['mode'] = meta.type;
                                        attrs['lock-items'] = '' + (meta.extra && meta.extra.lockItems || false);
                                        attrs['logger'] = '{{ :host.logger }}';
                                        if (!Utils.isNullOrEmpty(dependsOn)) {
                                            var extraDom = '';
                                            for (let d of dependsOn) {
                                                extraDom += `<${P}-childform-propagator model="${attrs["value"]}" watch="{{ :host.entity.${d.prop} }}" property="${(d.alias || d.prop)}"></${P}-childform-propagator>\n`;
                                            }
                                            this._container.innerHTML = extraDom;
                                        }
                                        attrs['fetch-credentials'] = '{{ :host.fetchCredentials }}';
                                        attrs['fetch-headers'] = '{{ :host.fetchHeaders }}';
                                    }
                                    break;
                            }
                            break;
                    }
                    break;
            }
        // #region commands
        if (!Utils.isNullOrEmpty(meta.commands)) {
            Utils.addClass(this._container, PCSS + '-fieldgroup');
            const prepend = this._container.appendChild(document.createElement('div')), append = this._container.appendChild(document.createElement('div'));
            Utils.addClass(prepend, `fieldgroup-prepend ${PCSS}-buttonset buttons`);
            Utils.addClass(append, `fieldgroup-append ${PCSS}-buttonset buttons`);
            const disable = dependingDisabling || 'false';
            meta.commands.forEach(cmd => {
                const btn = document.createElement(P + '-button');
                btn.setAttribute('icon-glyph', cmd.icon);
                btn.setAttribute('command-name', cmd.name);
                if (cmd.dependsOnValue) {
                    btn.setAttribute('disabled', `{{ (${disable}) || !::_form.valid || $pacem.isNullOrEmpty(:host.entity.${meta.prop}) }}`);
                    btn.setAttribute('command-argument', `{{ :host.entity.${meta.prop} }}`);
                }
                else {
                    btn.setAttribute('disabled', disabledAttr);
                }
                btn.setAttribute('tooltip', cmd.tooltip);
                if (!Utils.isNullOrEmpty(cmd.cssClass)) {
                    Utils.addClass(btn, cmd.cssClass.join(' '));
                }
                (cmd.prepend ? prepend : append).appendChild(btn);
            });
        }
        // #endregion
        // #region validators
        if (!Utils.isNullOrEmpty(meta.validators)) {
            meta.validators.forEach((v) => {
                var validatorElement;
                var params = {};
                var attributes = {};
                if (isKnownValidator(v)) {
                    params = v['params'] || {};
                    attributes['error-message'] = v.errorMessage;
                }
                if (isValidatorFactory(v)) {
                    // preserve static attributes, just in case
                    Utils.extend(attributes, v.attributes(this, ':host', ':host.entity'));
                }
                switch (v.type) {
                    case 'required':
                        attrs['required'] = 'true';
                        validatorElement = new PacemRequiredValidatorElement();
                        break;
                    case 'length':
                        let lengthValidator = new PacemLengthValidatorElement();
                        validatorElement = lengthValidator;
                        let max = params && params['max'];
                        let min = params && params['min'];
                        if (max != null) {
                            attrs['maxlength'] = '' + (lengthValidator.max = max);
                            if (tagName === P + '-input-chars') {
                                attrs['count'] ??= '' + max;
                            }
                        }
                        else if ('max' in attributes) {
                            attrs['max'] = attributes['max'];
                        }
                        if (min != null) {
                            attrs['minlength'] = '' + (lengthValidator.min = min);
                        }
                        else if ('min' in attributes) {
                            attrs['minlength'] = attributes['min'];
                        }
                        break;
                    case 'range':
                        let rangeValidator = new PacemRangeValidatorElement();
                        validatorElement = rangeValidator;
                        let maxNum = params && params['max'];
                        let minNum = params && params['min'];
                        let isDateTime = tagName === P + '-datetime-picker';
                        // TODO: handle calendar-picker
                        if (maxNum != null) {
                            rangeValidator.max = maxNum;
                            attrs['max'] = "{{ " + (isDateTime ? "'" + maxNum + "'" : maxNum) + " }}";
                        }
                        else if ('max' in attributes) {
                            attrs['max'] = attributes['max'];
                        }
                        if (minNum != null) {
                            rangeValidator.min = minNum;
                            attrs['min'] = "{{ " + (isDateTime ? "'" + minNum + "'" : minNum) + " }}";
                        }
                        else if ('min' in attributes) {
                            attrs['min'] = attributes['min'];
                        }
                        break;
                    case 'email':
                        let emailValidator = new PacemRegexValidatorElement();
                        validatorElement = emailValidator;
                        attrs['pattern'] = patternToString(emailValidator.pattern = /^[\w\-\.]+@[\w\-\.]+\.[a-zA-Z0-9]{2,6}$/);
                        break;
                    case 'regex':
                        let regexValidator = new PacemRegexValidatorElement();
                        validatorElement = regexValidator;
                        let pattern = params['pattern'];
                        pattern ||= attributes['pattern'];
                        attrs['pattern'] = patternToString(pattern);
                        regexValidator.pattern = pattern;
                        break;
                    case 'binary':
                        let binaryValidator = new PacemBinaryValidatorElement();
                        validatorElement = binaryValidator;
                        let filePattern = params['pattern'];
                        if (filePattern != null) {
                            attrs['pattern'] = patternToString(filePattern);
                            binaryValidator.pattern = filePattern;
                        }
                        else if ('pattern' in attributes) {
                            attrs['pattern'] = attributes['pattern'];
                        }
                        let maxSize = params && params['maxSize'];
                        if (maxSize != null) {
                            binaryValidator.maxSize = maxSize;
                            attrs['max-size'] = '' + maxSize;
                        }
                        else if ('max-size' in attributes) {
                            attrs['max-size'] = attributes['max-size'];
                        }
                        break;
                    case 'compare':
                        let compareValidator = new PacemCompareValidatorElement();
                        validatorElement = compareValidator;
                        let comparedToCore = '';
                        if ('value' in params) {
                            comparedToCore = JSON.stringify(params.value);
                        }
                        else {
                            const toProp = 'to' in params ? params.to : params.toProperty;
                            comparedToCore = `:host.entity.${toProp}`;
                        }
                        const compareToAttr = `{{ ${comparedToCore} }}`;
                        compareValidator.setAttribute('to', compareToAttr);
                        let operator = compareValidator.operator = params['operator'] || attributes['operator'] || 'equal';
                        // In case of `date(time)` try to add some interaction with `datetime-picker`'s min/max props.
                        if (tagName === P + '-datetime-picker') {
                            switch (operator) {
                                case 'lessOrEqual':
                                case 'less': // approximation: edge value won't be allowed but will result enabled on the `datetime-picker`
                                    attrs['max'] = compareToAttr;
                                    break;
                                case 'greaterOrEqual':
                                case 'greater': // approximation: edge value won't be allowed but will result enabled on the `datetime-picker`
                                    attrs['min'] = compareToAttr;
                                    break;
                            }
                        } /* // TODO: should accumulate disabled ranges (possible multiple validators)
                         else if (tagName === P + '-calendar-picker') {
                            //
                            switch (operator) {
                                case 'lessOrEqual':
                                case 'less': // approximation: edge value won't be allowed but will result enabled on the `calendar-picker`
                                    attrs['disabled-ranges'] = `{{ [{ to: ${comparedToCore} }] }}`;
                                    break;
                                case 'greaterOrEqual':
                                case 'greater': // approximation: edge value won't be allowed but will result enabled on the `calendar-picker`
                                    attrs['disabled-ranges'] = `{{ [{ from: ${comparedToCore} }] }}`;
                                    break;
                            }
                        }*/
                        break;
                    case 'async':
                        let asyncValidator = new PacemAsyncValidatorElement();
                        validatorElement = asyncValidator;
                        asyncValidator.url = params['url'];
                        let parameters = [], depOn = params['dependsOn'] || dependsOn;
                        parameters.push(`${meta.prop}: :host._adjustDependencyValue(:host.entity.${meta.prop})`);
                        for (let depend of depOn || []) {
                            parameters.push(`${(depend.alias || depend.prop)}: :host._adjustDependencyValue(:host.entity.${depend.prop})`);
                        }
                        asyncValidator.setAttribute('parameters', `{{ { ${(parameters.join(', '))} } }}`);
                        asyncValidator.method = params['verb'] || Net.HttpMethod.Get;
                        asyncValidator.setAttribute('fetch-credentials', '{{ :host.fetchCredentials }}');
                        asyncValidator.setAttribute('fetch-headers', '{{ :host.fetchHeaders }}');
                        break;
                    case 'custom':
                        validatorElement = new PacemCustomValidatorElement();
                        break;
                    default:
                        // custom (extra-lib) validator
                        if (!Utils.isNullOrEmpty(v.type) && /* must be a custom element */ v.type.indexOf('-') > 0) {
                            const tagName = v.type;
                            const extern = document.createElement(tagName);
                            if (extern instanceof PacemBaseValidatorElement) {
                                validatorElement = extern;
                            }
                        }
                        break;
                }
                //
                if (Utils.isNull(validatorElement)) {
                    throw `Cannot generate a formfield validator based on type: ${v.type}.`;
                }
                else {
                    validatorElement.watch = meta.prop;
                    validatorElement.setAttribute('hide', '{{ !this.invalid }}');
                    validatorElement.setAttribute('disabled', disabledAttr);
                    for (let attrName in attributes) {
                        validatorElement.setAttribute(attrName, attributes[attrName]);
                    }
                    this._validators.appendChild(validatorElement);
                }
            });
        }
        // #endregion
        // #region build-up
        let field = document.createElement(tagName);
        for (var name in attrs) {
            let attr;
            if (!Utils.isNullOrEmpty(attr = attrs[name]))
                field.setAttribute(name, attr);
        }
        field.append(children);
        // #endregion
        this._field = field;
        this._container.insertBefore(field, this._container.firstElementChild);
        for (var name in fetchAttrs) {
            let attr;
            if (!Utils.isNullOrEmpty(attr = fetchAttrs[name]))
                this._fetcher.setAttribute(name, attr);
        }
        // fire propertychange event for bindings' sake...
        this.dispatchEvent(new PropertyChangeEvent({ propertyName: 'field', currentValue: field }));
    }
};
__decorate([
    ViewChild('label')
], PacemFormFieldElement.prototype, "_label", void 0);
__decorate([
    ViewChild(`div.${PCSS}-input-container`)
], PacemFormFieldElement.prototype, "_container", void 0);
__decorate([
    ViewChild(P + '-fetch')
], PacemFormFieldElement.prototype, "_fetcher", void 0);
__decorate([
    ViewChild(P + '-form')
], PacemFormFieldElement.prototype, "_form", void 0);
__decorate([
    ViewChild(P + '-panel.' + PCSS + '-validators')
], PacemFormFieldElement.prototype, "_validators", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Json })
], PacemFormFieldElement.prototype, "metadata", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Json })
], PacemFormFieldElement.prototype, "fetchHeaders", void 0);
__decorate([
    Watch({ converter: PropertyConverters.String })
], PacemFormFieldElement.prototype, "fetchCredentials", void 0);
__decorate([
    Watch({ converter: PropertyConverters.Boolean })
], PacemFormFieldElement.prototype, "readonly", void 0);
__decorate([
    Watch()
], PacemFormFieldElement.prototype, "entity", void 0);
PacemFormFieldElement = __decorate([
    CustomElement({
        tagName: P + '-form-field', template: `<${P}-form class="${PCSS}-field" logger="{{ :host.logger }}" 
css-class="{{ {'${PCSS}-fetching': ::_fetcher.fetching, '${PCSS}-dirty': this.dirty, '${PCSS}-invalid': !this.valid, '${PCSS}-editable': !:host.readonly, '${PCSS}-readonly': :host.readonly, '${PCSS}-pristine': !this.dirty, '${PCSS}-valid': this.valid, '${PCSS}-has-value': !:host._isValueNullOrEmpty(:host.entity, :host.metadata) } }}">
    <label class="${PCSS}-label"><${P}-span></${P}-span></label>
    <div class="${PCSS}-input-container"></div>
    <${P}-fetch debounce="300" logger="{{ :host.logger }}" credentials="{{ :host.fetchCredentials }}" headers="{{ :host.fetchHeaders }}" diff-by-values="true"
    on-fetchresult=":host._broadcastFetchResult($event.detail)"></${P}-fetch>
    <${P}-panel class="${PCSS}-validators" hide="{{ ::_form.valid || !::_form.dirty || :host.readonly }}"></${P}-panel>
</${P}-form>`
    })
], PacemFormFieldElement);
export { PacemFormFieldElement };
